import { useState, useEffect, useMemo } from "react";
import api from "../../services/api";
import { imprimirComprobante } from "../../components/Comprobante";
import { parseFechaLocal, formatPorcionesOCantidad } from "../../utils/pedidoDisplay";

const TIPOS_VENTA_FIJOS = ["precio_fijo", "precio_por_unidad", "paquete"];

const estadoConfig = {
  pendiente: { label: "Pendiente", color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200" },
  listo: { label: "Entregado", color: "text-green-700", bg: "bg-green-50", border: "border-green-200" },
  cancelado: { label: "Cancelado", color: "text-red-600", bg: "bg-red-50", border: "border-red-200" },
};

const MESES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const DIAS_SEMANA = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

const keyFecha = (fecha) => {
  const d = parseFechaLocal(fecha);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
};

export default function MisPedidos() {
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("pendiente");
  const [modalPago, setModalPago] = useState(null);
  const [montoPago, setMontoPago] = useState("");
  const [metodoPago, setMetodoPago] = useState("efectivo");

  const [modalEditar, setModalEditar] = useState(null);
  const [formEditar, setFormEditar] = useState({});

  const hoy = new Date();
  const [mesActual, setMesActual] = useState(hoy.getMonth());
  const [anioActual, setAnioActual] = useState(hoy.getFullYear());
  const [diaSeleccionado, setDiaSeleccionado] = useState(keyFecha(hoy));

  useEffect(() => {
    cargarPedidos();
    const interval = setInterval(cargarPedidos, 15000);
    return () => clearInterval(interval);
  }, []);

  const cargarPedidos = async () => {
    try {
      const res = await api.get("/pedidos");
      setPedidos(res.data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const marcarEntregado = async (pedido) => {
    const saldo = parseInt(pedido.saldo || 0);
    if (saldo > 0) {
      alert(`No se puede marcar como entregado: aún tiene un saldo pendiente de $${saldo.toLocaleString("es-CL")}. Cobra el saldo primero.`);
      return;
    }
    try {
      await api.patch(`/pedidos/${pedido.id}/estado`, { estado: "listo" });
      cargarPedidos();
    } catch (err) {
      console.error(err);
      alert("Error al actualizar el estado");
    }
  };

  const cancelarPedido = async (pedido) => {
    if (!window.confirm(`¿Cancelar el pedido ${pedido.numero_pedido} de ${pedido.cliente_nombre}? Esta acción no se puede deshacer.`)) return;
    try {
      await api.patch(`/pedidos/${pedido.id}/estado`, { estado: "cancelado" });
      cargarPedidos();
    } catch (err) {
      console.error(err);
      alert("Error al cancelar el pedido");
    }
  };

  const abrirEditar = (pedido) => {
    setModalEditar(pedido);
    setFormEditar({
      cliente_nombre: pedido.cliente_nombre ?? "",
      cliente_telefono: pedido.cliente_telefono ?? "",
      porcion: pedido.porcion ?? "",
      formato: pedido.formato ?? "",
      cantidad: pedido.cantidad ?? 1,
      decoracion: pedido.decoracion ?? "ninguno",
      detalle_especial: pedido.detalle_especial || "",
      mensaje_torta: pedido.mensaje_torta || "",
      fecha_entrega: pedido.fecha_entrega?.slice(0, 10),
      hora_entrega: pedido.hora_entrega?.slice(0, 5),
    });
  };

  const guardarEdicion = async () => {
    if (!formEditar.cliente_nombre || !formEditar.fecha_entrega || !formEditar.hora_entrega) {
      alert("Completa nombre, fecha y hora");
      return;
    }
    try {
      await api.put(`/pedidos/${modalEditar.id}`, formEditar);
      setModalEditar(null);
      cargarPedidos();
    } catch (err) {
      console.error("Error editando pedido:", err.response?.data || err.message);
      alert("Error al guardar cambios: " + (err.response?.data?.message || "intenta de nuevo"));
    }
  };

  const enviarWhatsApp = (pedido) => {
    const msg = encodeURIComponent(
      `Hola ${pedido.cliente_nombre}! 🌾 *BANCHER*\n\n` +
        `✅ Tu pedido está *LISTO* para retirar!\n` +
        `🎂 ${pedido.producto} — ${formatPorcionesOCantidad(pedido)}\n` +
        `📍 Retira en Local ${pedido.local_origen?.toUpperCase()}\n` +
        `${parseInt(pedido.saldo) > 0 ? `💰 Recuerda traer el saldo: $${parseInt(pedido.saldo).toLocaleString("es-CL")}` : "✓ Ya está todo pagado"}\n\n¡Gracias por preferirnos!`,
    );
    const telefono = pedido.cliente_telefono?.replace(/\D/g, "");
    window.open(`https://wa.me/56${telefono}?text=${msg}`, "_blank");
  };

  const registrarPago = async () => {
    if (!montoPago || !modalPago) return;
    try {
      await api.patch(`/pedidos/${modalPago.id}/pago`, { monto: parseInt(montoPago), metodo_pago: metodoPago });
      setModalPago(null);
      setMontoPago("");
      setMetodoPago("efectivo");
      cargarPedidos();
    } catch (err) {
      console.error("Error al cobrar saldo:", err.response?.data || err.message);
      alert("Error al registrar el pago: " + (err.response?.data?.message || "intenta de nuevo"));
    }
  };

  const pedidosPorDia = useMemo(() => {
    const mapa = {};
    pedidos.forEach((p) => {
      if (!p.fecha_entrega) return;
      const key = keyFecha(p.fecha_entrega);
      if (!mapa[key]) mapa[key] = [];
      mapa[key].push(p);
    });
    return mapa;
  }, [pedidos]);

  const diasDelMes = useMemo(() => {
    const primerDia = new Date(anioActual, mesActual, 1);
    const ultimoDia = new Date(anioActual, mesActual + 1, 0);
    const dias = [];
    for (let i = 0; i < primerDia.getDay(); i++) dias.push(null);
    for (let d = 1; d <= ultimoDia.getDate(); d++) dias.push(d);
    return dias;
  }, [mesActual, anioActual]);

  const cambiarMes = (delta) => {
    let nuevoMes = mesActual + delta;
    let nuevoAnio = anioActual;
    if (nuevoMes < 0) { nuevoMes = 11; nuevoAnio--; }
    else if (nuevoMes > 11) { nuevoMes = 0; nuevoAnio++; }
    setMesActual(nuevoMes);
    setAnioActual(nuevoAnio);
  };

  const esHoy = (dia) => dia === hoy.getDate() && mesActual === hoy.getMonth() && anioActual === hoy.getFullYear();

  const handleClickDia = (dia) => {
    if (!dia) return;
    setDiaSeleccionado(`${anioActual}-${mesActual}-${dia}`);
  };

  const pedidosDelDia = (pedidosPorDia[diaSeleccionado] || []).filter(
    (p) => filtro === "todos" || p.estado === filtro
  );

  const fechaSeleccionadaLegible = useMemo(() => {
    if (!diaSeleccionado) return null;
    const [anio, mes, dia] = diaSeleccionado.split("-").map(Number);
    return new Date(anio, mes, dia).toLocaleDateString("es-CL", { weekday: "long", day: "numeric", month: "long" });
  }, [diaSeleccionado]);

  const inputEditClass = "w-full mt-1 bg-[#E4E9F2] border border-[#E5E9F0] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#1A56DB] focus:ring-1 focus:ring-[#1A56DB]";
  const labelEditClass = "text-xs text-gray-500 uppercase tracking-wider font-medium";

  if (loading) {
    return (
      <div className="min-h-screen bg-[#E4E9F2] flex items-center justify-center">
        <div className="flex items-center gap-3 text-[#1A56DB]">
          <div className="w-5 h-5 border-2 border-[#1A56DB] border-t-transparent rounded-full animate-spin"></div>
          <span className="text-sm font-medium">Cargando pedidos...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#E4E9F2] p-4">
      <div className="flex gap-2 mb-4 flex-wrap">
        {["todos", "pendiente", "listo", "cancelado"].map((f) => {
          const colorPunto = f === "pendiente" ? "bg-[#1A56DB]" : f === "listo" ? "bg-green-500" : f === "cancelado" ? "bg-red-400" : null;
          const activo = filtro === f;
          return (
            <button
              key={f}
              onClick={() => setFiltro(f)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${
                activo ? "bg-[#1A56DB] text-white" : "bg-white text-gray-600 border border-[#E5E9F0] hover:border-[#1A56DB] hover:text-[#1A56DB]"
              }`}
            >
              {colorPunto && (
                <span className={`w-1.5 h-1.5 rounded-full ${activo ? "bg-white" : colorPunto}`} />
              )}
              {f === "todos" ? `Todos (${pedidos.length})` : `${estadoConfig[f]?.label || f} (${pedidos.filter((p) => p.estado === f).length})`}
            </button>
          );
        })}
      </div>

      <div className="bg-white border border-[#E5E9F0] rounded-xl p-4 shadow-sm">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="w-full lg:w-[340px] flex-shrink-0">
            <div className="flex items-center justify-between mb-4">
              <button onClick={() => cambiarMes(-1)} className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#E5E9F0] hover:bg-[#E4E9F2] text-gray-600">‹</button>
              <h3 className="text-sm font-semibold text-gray-900">{MESES[mesActual]} {anioActual}</h3>
              <button onClick={() => cambiarMes(1)} className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#E5E9F0] hover:bg-[#E4E9F2] text-gray-600">›</button>
            </div>

            <div className="grid grid-cols-7 gap-1 mb-1">
              {DIAS_SEMANA.map((d) => (
                <div key={d} className="text-center text-xs font-medium text-gray-400 py-1">{d}</div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {diasDelMes.map((dia, i) => {
                if (!dia) return <div key={i} />;
                const key = `${anioActual}-${mesActual}-${dia}`;
                const pedidosDiaCompleto = pedidosPorDia[key] || [];
                const pedidosDia = filtro === "todos" ? pedidosDiaCompleto : pedidosDiaCompleto.filter((p) => p.estado === filtro);
                const seleccionado = diaSeleccionado === key;
                const hayPendientes = pedidosDia.some((p) => p.estado === "pendiente");
                const todosEntregados = pedidosDia.length > 0 && pedidosDia.every((p) => p.estado === "listo");
                const todosCancelados = pedidosDia.length > 0 && pedidosDia.every((p) => p.estado === "cancelado");

                const colorFondo = pedidosDia.length === 0
                  ? null
                  : hayPendientes
                  ? "bg-[#1A56DB] text-white"
                  : todosEntregados
                  ? "bg-green-500 text-white"
                  : todosCancelados
                  ? "bg-red-400 text-white"
                  : "bg-gray-300 text-gray-700";

                return (
                  <button
                    key={i}
                    onClick={() => handleClickDia(dia)}
                    className={`aspect-square rounded-lg text-xs font-medium relative flex items-center justify-center transition-all ${
                      seleccionado
                        ? "ring-2 ring-offset-1 ring-[#0F1F4D]"
                        : ""
                      } ${
                      colorFondo
                        ? colorFondo
                        : esHoy(dia)
                        ? "bg-blue-50 text-[#1A56DB] border border-[#1A56DB]"
                        : "text-gray-400 hover:bg-gray-100"
                      }`}
                  >
                    {dia}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex-1 lg:border-l lg:border-[#E5E9F0] lg:pl-4 min-w-0">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
              {pedidosDelDia.length} pedido{pedidosDelDia.length !== 1 ? "s" : ""}
            </h4>
            <p className="text-sm font-medium text-gray-900 capitalize mb-3">{fechaSeleccionadaLegible}</p>

            {pedidosDelDia.length === 0 && (
              <div className="text-xs text-gray-400 text-center py-8 bg-[#E4E9F2] rounded-lg border border-dashed border-gray-300">
                Sin pedidos este día
              </div>
            )}

            <div className="space-y-3">
              {pedidosDelDia
                .sort((a, b) => (a.hora_entrega || "").localeCompare(b.hora_entrega || ""))
                .map((pedido) => {
                  const sc = estadoConfig[pedido.estado] || estadoConfig.pendiente;
                  const saldo = parseInt(pedido.saldo || 0);
                  return (
                    <div key={pedido.id} className="bg-[#E4E9F2] border border-[#E5E9F0] rounded-lg p-3">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-semibold text-[#1A56DB]">{pedido.numero_pedido}</span>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${sc.bg} ${sc.color} border ${sc.border}`}>{sc.label}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${pedido.local_origen === "sur" ? "bg-blue-50 text-blue-700 border border-blue-200" : "bg-gray-100 text-gray-600 border border-[#E5E9F0]"}`}>
                            {pedido.local_origen?.toUpperCase()}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500 font-medium">{pedido.hora_entrega?.slice(0, 5)} hrs</span>
                      </div>

                      <div className="text-sm font-semibold text-gray-900">{pedido.cliente_nombre}</div>
                      <div className="text-xs text-gray-500">{pedido.cliente_telefono}</div>
                      <div className="text-xs text-[#1A56DB] font-medium mt-1">🎂 {pedido.producto} · {formatPorcionesOCantidad(pedido)}</div>
                      {Array.isArray(pedido.variedades) && pedido.variedades.length > 0 && (
                        <div className="text-xs text-blue-600 font-medium mt-0.5">Variedades: {pedido.variedades.join(', ')}</div>
                      )}
                      {pedido.detalle_especial && <div className="text-xs text-amber-600 mt-0.5">📝 {pedido.detalle_especial}</div>}

                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#E5E9F0]">
                        <div>
                          <div className="text-sm font-semibold text-gray-900">${parseInt(pedido.precio_total).toLocaleString("es-CL")}</div>
                          <div className={`text-xs font-semibold ${saldo > 0 ? "text-amber-600" : "text-green-600"}`}>
                            {saldo > 0 ? `Saldo: $${saldo.toLocaleString("es-CL")}` : "✓ Pagado"}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap justify-end">
                          {pedido.estado === "pendiente" && (
                            <>
                              <button onClick={() => abrirEditar(pedido)} className="bg-white border border-[#E5E9F0] hover:border-[#1A56DB] text-gray-600 hover:text-[#1A56DB] text-xs font-medium px-2.5 py-1.5 rounded-lg transition-all">
                                Editar
                              </button>
                              <button onClick={() => enviarWhatsApp(pedido)} className="bg-[#25D366] hover:bg-[#128C7E] text-white text-xs font-medium px-2.5 py-1.5 rounded-lg transition-all">
                                Avisar
                              </button>
                              <button onClick={() => marcarEntregado(pedido)} className="bg-[#1A56DB] hover:bg-[#1E429F] text-white text-xs font-medium px-2.5 py-1.5 rounded-lg transition-all">
                                Entregado
                              </button>
                              <button onClick={() => cancelarPedido(pedido)} className="bg-red-50 border border-red-200 hover:bg-red-100 text-red-600 text-xs font-medium px-2.5 py-1.5 rounded-lg transition-all">
                                Cancelar
                              </button>
                            </>
                          )}
                          {saldo > 0 && pedido.estado !== "cancelado" && (
                            <button onClick={() => { setModalPago(pedido); setMontoPago(saldo.toString()); }} className="bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium px-2.5 py-1.5 rounded-lg transition-all">
                              Cobrar
                            </button>
                          )}
                          <button onClick={() => imprimirComprobante(pedido)} className="bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-medium p-1.5 rounded-lg transition-all" title="Reimprimir">
                            🖨️
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      </div>

      {modalPago && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center backdrop-blur-sm p-4">
          <div className="bg-white border border-[#E5E9F0] rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <h3 className="text-gray-900 font-semibold text-lg mb-1">Cobrar saldo</h3>
            <p className="text-gray-400 text-sm mb-4">{modalPago.cliente_nombre} · {modalPago.numero_pedido}</p>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
              <div className="text-xs text-amber-600 font-medium">Saldo pendiente</div>
              <div className="text-2xl font-bold text-amber-700 mt-1">${parseInt(modalPago.saldo).toLocaleString("es-CL")}</div>
            </div>

            <div className="mb-4">
              <label className="text-xs text-gray-500 uppercase tracking-wider font-medium">Monto recibido ($)</label>
              <input type="number" value={montoPago} onChange={(e) => setMontoPago(e.target.value)}
                className="w-full mt-1 bg-[#E4E9F2] border border-[#E5E9F0] rounded-lg px-4 py-3 text-gray-900 text-lg font-semibold focus:outline-none focus:border-[#1A56DB] focus:ring-1 focus:ring-[#1A56DB] transition-all" />
            </div>

            <div className="mb-4">
              <label className="text-xs text-gray-500 uppercase tracking-wider font-medium">Método de pago</label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                {[{ key: "efectivo", label: "Efectivo", icon: "💵" }, { key: "tarjeta", label: "Tarjeta", icon: "💳" }].map((m) => (
                  <button key={m.key} type="button" onClick={() => setMetodoPago(m.key)}
                    className={`py-2.5 rounded-lg text-sm font-medium border transition-all flex items-center justify-center gap-2 ${
                      metodoPago === m.key ? "bg-[#1A56DB] text-white border-[#1A56DB]" : "bg-[#E4E9F2] text-gray-500 border-[#E5E9F0] hover:border-[#1A56DB]"
                    }`}>
                    <span>{m.icon}</span><span>{m.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-2">
              <button onClick={registrarPago} className="flex-1 bg-[#1A56DB] hover:bg-[#1E429F] text-white font-semibold py-3 rounded-lg transition-all">Confirmar pago</button>
              <button onClick={() => { setModalPago(null); setMontoPago(""); setMetodoPago("efectivo"); }} className="bg-gray-100 hover:bg-gray-200 text-gray-600 px-4 rounded-lg transition-all">Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {modalEditar && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center backdrop-blur-sm p-4">
          <div className="bg-white border border-[#E5E9F0] rounded-2xl p-6 w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-gray-900 font-semibold text-lg mb-1">Editar pedido</h3>
            <p className="text-gray-400 text-sm mb-4">{modalEditar.numero_pedido} · {modalEditar.producto}</p>

            <div className="space-y-3">
              <div>
                <label className={labelEditClass}>Nombre cliente</label>
                <input type="text" value={formEditar.cliente_nombre} onChange={(e) => setFormEditar({ ...formEditar, cliente_nombre: e.target.value })} className={inputEditClass} />
              </div>
              <div>
                <label className={labelEditClass}>Teléfono</label>
                <input type="tel" value={formEditar.cliente_telefono} onChange={(e) => setFormEditar({ ...formEditar, cliente_telefono: e.target.value })} className={inputEditClass} />
              </div>
              {TIPOS_VENTA_FIJOS.includes(modalEditar.tipo_venta) ? (
                <div>
                  <label className={labelEditClass}>Cantidad</label>
                  <input type="number" min="1" value={formEditar.cantidad} onChange={(e) => setFormEditar({ ...formEditar, cantidad: e.target.value })} className={inputEditClass} />
                </div>
              ) : (
                <div>
                  <label className={labelEditClass}>Porciones</label>
                  <input type="number" value={formEditar.porcion} onChange={(e) => setFormEditar({ ...formEditar, porcion: e.target.value })} className={inputEditClass} />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelEditClass}>Fecha</label>
                  <input type="date" value={formEditar.fecha_entrega} onChange={(e) => setFormEditar({ ...formEditar, fecha_entrega: e.target.value })} className={inputEditClass} />
                </div>
                <div>
                  <label className={labelEditClass}>Hora</label>
                  <input type="time" value={formEditar.hora_entrega} onChange={(e) => setFormEditar({ ...formEditar, hora_entrega: e.target.value })} className={inputEditClass} />
                </div>
              </div>
              <div>
                <label className={labelEditClass}>Mensaje para la torta</label>
                <input type="text" value={formEditar.mensaje_torta} onChange={(e) => setFormEditar({ ...formEditar, mensaje_torta: e.target.value })} className={inputEditClass} />
              </div>
              <div>
                <label className={labelEditClass}>Detalle especial</label>
                <input type="text" value={formEditar.detalle_especial} onChange={(e) => setFormEditar({ ...formEditar, detalle_especial: e.target.value })} className={inputEditClass} />
              </div>
            </div>

            <div className="flex gap-2 mt-5">
              <button onClick={guardarEdicion} className="flex-1 bg-gradient-to-r from-[#0F1F4D] to-[#1A56DB] text-white font-semibold py-2.5 rounded-lg">
                Guardar cambios
              </button>
              <button onClick={() => setModalEditar(null)} className="bg-gray-100 hover:bg-gray-200 text-gray-600 px-4 rounded-lg">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
